import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';

import ProgressPanel from './components/ProgressPanel';
import FilterControls from './components/FilterControls';
import MissionListView from './components/MissionListView';

import Button from '../../components/ui/Button';

const MissionMapDashboard = () => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('map');
  const [selectedMission, setSelectedMission] = useState(null);

  // Mock user data
  const currentUser = {
    id: 1,
    username: "selenium_learner",
    email: "learner@example.com",
    level: 3,
    xp: 1250,
    nextLevelXp: 2000
  };

  // Mock user statistics
  const userStats = {
    missionsCompleted: 12,
    currentStreak: 7,
    dailyStreak: 7,
    bossesDefeated: 2,
    level: 3,
    xp: 1250,
    nextLevelXp: 2000,
    skillLevels: {
      elementSelection: 4,
      waits: 3,
      actions: 2,
      pageObjects: 1
    }
  };

  // Mock mission data
  const missionPaths = [
    {
      id: 1,
      title: "Beginner Track",
      description: "Start your Selenium journey with fundamental concepts and basic automation techniques.",
      category: "beginner",
      totalPoints: 500,
      estimatedHours: 8,
      completedMissions: 4,
      missions: [
        {
          id: 1,
          title: "First Steps",
          description: "Learn to set up Selenium and write your first automation script",
          difficulty: "beginner",
          status: "completed",
          progress: 100,
          estimatedTime: "30 min",
          points: 50,
          completedBy: 1247,
          thumbnail: "https://images.pexels.com/photos/1181671/pexels-photo-1181671.jpeg?auto=compress&cs=tinysrgb&w=400",
          tags: ["setup", "basics"],
          isBookmarked: false
        },
        {
          id: 2,
          title: "Element Locators",
          description: "Master different strategies for finding elements on web pages",
          difficulty: "beginner",
          status: "completed",
          progress: 100,
          estimatedTime: "45 min",
          points: 75,
          completedBy: 1156,
          thumbnail: "https://images.pexels.com/photos/1181244/pexels-photo-1181244.jpeg?auto=compress&cs=tinysrgb&w=400",
          tags: ["locators", "xpath"],
          isBookmarked: true
        },
        {
          id: 3,
          title: "Basic Interactions",
          description: "Learn to click, type, and interact with web elements",
          difficulty: "beginner",
          status: "in-progress",
          progress: 65,
          estimatedTime: "40 min",
          points: 60,
          completedBy: 1089,
          thumbnail: "https://images.pexels.com/photos/1181298/pexels-photo-1181298.jpeg?auto=compress&cs=tinysrgb&w=400",
          tags: ["interactions", "forms"],
          isBookmarked: false
        },
        {
          id: 4,
          title: "Form Handling",
          description: "Master form submissions and input validation",
          difficulty: "beginner",
          status: "available",
          progress: 0,
          estimatedTime: "50 min",
          points: 80,
          completedBy: 967,
          thumbnail: "https://images.pexels.com/photos/1181354/pexels-photo-1181354.jpeg?auto=compress&cs=tinysrgb&w=400",
          tags: ["forms", "validation"],
          isBookmarked: false
        }
      ]
    },
    {
      id: 2,
      title: "Intermediate Challenges",
      description: "Advance your skills with complex scenarios and dynamic content handling.",
      category: "intermediate",
      totalPoints: 800,
      estimatedHours: 12,
      completedMissions: 2,
      missions: [
        {
          id: 5,
          title: "Dynamic Content",
          description: "Handle dynamically loaded content and AJAX requests",
          difficulty: "intermediate",
          status: "completed",
          progress: 100,
          estimatedTime: "60 min",
          points: 100,
          completedBy: 743,
          thumbnail: "https://images.pexels.com/photos/1181467/pexels-photo-1181467.jpeg?auto=compress&cs=tinysrgb&w=400",
          tags: ["ajax", "dynamic"],
          isBookmarked: true
        },
        {
          id: 6,
          title: "Wait Strategies",
          description: "Master explicit and implicit waits for reliable automation",
          difficulty: "intermediate",
          status: "completed",
          progress: 100,
          estimatedTime: "55 min",
          points: 120,
          completedBy: 689,
          thumbnail: "https://images.pexels.com/photos/1181533/pexels-photo-1181533.jpeg?auto=compress&cs=tinysrgb&w=400",
          tags: ["waits", "timing"],
          isBookmarked: false
        },
        {
          id: 7,
          title: "Multi-Window Handling",
          description: "Navigate between multiple browser windows and tabs",
          difficulty: "intermediate",
          status: "locked",
          progress: 0,
          estimatedTime: "70 min",
          points: 140,
          completedBy: 567,
          thumbnail: "https://images.pexels.com/photos/1181605/pexels-photo-1181605.jpeg?auto=compress&cs=tinysrgb&w=400",
          tags: ["windows", "tabs"],
          isBookmarked: false
        },
        {
          id: 8,
          title: "Frame Navigation",
          description: "Work with iframes and nested content structures",
          difficulty: "intermediate",
          status: "locked",
          progress: 0,
          estimatedTime: "65 min",
          points: 130,
          completedBy: 445,
          thumbnail: "https://images.pexels.com/photos/1181677/pexels-photo-1181677.jpeg?auto=compress&cs=tinysrgb&w=400",
          tags: ["frames", "iframes"],
          isBookmarked: false
        }
      ]
    },
    {
      id: 3,
      title: "Advanced Automation",
      description: "Master complex automation patterns and advanced Selenium features.",
      category: "advanced",
      totalPoints: 1200,
      estimatedHours: 18,
      completedMissions: 0,
      missions: [
        {
          id: 9,
          title: "Page Object Model",
          description: "Implement maintainable test architecture with Page Object Model",
          difficulty: "advanced",
          status: "locked",
          progress: 0,
          estimatedTime: "90 min",
          points: 200,
          completedBy: 234,
          thumbnail: "https://images.pexels.com/photos/1181712/pexels-photo-1181712.jpeg?auto=compress&cs=tinysrgb&w=400",
          tags: ["pom", "architecture"],
          isBookmarked: false
        },
        {
          id: 10,
          title: "Data-Driven Testing",
          description: "Create flexible tests using external data sources",
          difficulty: "advanced",
          status: "locked",
          progress: 0,
          estimatedTime: "85 min",
          points: 180,
          completedBy: 189,
          thumbnail: "https://images.pexels.com/photos/1181748/pexels-photo-1181748.jpeg?auto=compress&cs=tinysrgb&w=400",
          tags: ["data-driven", "csv"],
          isBookmarked: false
        }
      ]
    },
    {
      id: 4,
      title: "Boss Missions",
      description: "Ultimate challenges that test all your Selenium skills in complex scenarios.",
      category: "boss",
      totalPoints: 2000,
      estimatedHours: 25,
      completedMissions: 1,
      missions: [
        {
          id: 11,
          title: "E-commerce Automation",
          description: "Automate a complete e-commerce workflow from search to checkout",
          difficulty: "boss",
          status: "completed",
          progress: 100,
          estimatedTime: "120 min",
          points: 500,
          completedBy: 67,
          thumbnail: "https://images.pexels.com/photos/1181784/pexels-photo-1181784.jpeg?auto=compress&cs=tinysrgb&w=400",
          tags: ["e-commerce", "workflow"],
          isBookmarked: true
        },
        {
          id: 12,
          title: "Banking System Test",
          description: "Navigate complex banking interfaces with security challenges",
          difficulty: "boss",
          status: "locked",
          progress: 0,
          estimatedTime: "150 min",
          points: 750,
          completedBy: 23,
          thumbnail: "https://images.pexels.com/photos/1181820/pexels-photo-1181820.jpeg?auto=compress&cs=tinysrgb&w=400",
          tags: ["banking", "security"],
          isBookmarked: false
        }
      ]
    }
  ];

  // Filter missions based on active filter and search query
  const getFilteredMissions = () => {
    let allMissions = missionPaths?.flatMap(path => path?.missions);
    
    // Apply category filter
    if (activeFilter !== 'all') {
      if (activeFilter === 'completed' || activeFilter === 'in-progress') {
        allMissions = allMissions?.filter(mission => mission?.status === activeFilter);
      } else if (activeFilter === 'bookmarked') {
        allMissions = allMissions?.filter(mission => mission?.isBookmarked);
      } else {
        allMissions = allMissions?.filter(mission => mission?.difficulty === activeFilter);
      }
    }

    // Apply search filter
    if (searchQuery) {
      allMissions = allMissions?.filter(mission =>
        mission?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        mission?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        mission?.tags?.some(tag => tag?.toLowerCase()?.includes(searchQuery?.toLowerCase()))
      );
    }

    return allMissions;
  };

  const handleMissionClick = (mission) => {
    if (mission?.status !== 'locked') {
      setSelectedMission(mission);
      // Navigate to mission workspace with mission data
      navigate('/mission-workspace', { state: { mission } });
    }
  };

  const handleLogout = () => {
    // Handle logout logic
    navigate('/user-login');
  };

  useEffect(() => {
    // Scroll to top on component mount
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header 
        user={currentUser} 
        onLogout={handleLogout}
      />
      <div className="content-offset">
        <div className="container mx-auto px-4 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">Mission List</h1>
                <p className="text-muted-foreground">
                  Master Selenium automation through hands-on missions and challenges
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  iconName="BookOpen"
                  iconPosition="left"
                  onClick={() => navigate('/mission-results-summary')}
                >
                  Learning Stats
                </Button>
                <Button
                  variant="default"
                  iconName="User"
                  iconPosition="left"
                  onClick={() => navigate('/user-profile-dashboard')}
                >
                  Profile
                </Button>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-card border border-subtle rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-primary mb-1">{userStats?.missionsCompleted}</div>
                <div className="text-sm text-muted-foreground">Completed</div>
              </div>
              <div className="bg-card border border-subtle rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-success mb-1">{userStats?.currentStreak}</div>
                <div className="text-sm text-muted-foreground">Day Streak</div>
              </div>
              <div className="bg-card border border-subtle rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-warning mb-1">Level {userStats?.level}</div>
                <div className="text-sm text-muted-foreground">Current Level</div>
              </div>
              <div className="bg-card border border-subtle rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-secondary mb-1">{userStats?.bossesDefeated}</div>
                <div className="text-sm text-muted-foreground">Bosses Defeated</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              <FilterControls
                activeFilter={activeFilter}
                onFilterChange={setActiveFilter}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
              />
              
              <div className="hidden lg:block">
                <ProgressPanel userStats={userStats} />
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-foreground">
                    {activeFilter === 'all' ? 'All Missions' : `${activeFilter?.charAt(0)?.toUpperCase() + activeFilter?.slice(1)} Missions`}
                  </h2>
                  <div className="text-sm text-muted-foreground">
                    {getFilteredMissions()?.length} missions found
                  </div>
                </div>
                <MissionListView
                  missions={getFilteredMissions()}
                  onMissionClick={handleMissionClick}
                />
              </div>
            </div>
          </div>

          {/* Mobile Progress Panel */}
          <div className="lg:hidden mt-8">
            <ProgressPanel userStats={userStats} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MissionMapDashboard;